import React, { useState } from "react";
import Axios from "axios";
import { QuestionnaireForm } from "../../../components";
import { buildInfoQuestionnaire } from "../../../utils/personal-info-constants";
import useAuthUser from "../../../hooks/auth-user";
import useLanguage from "../../../hooks/language";
import Questionnaire from "../../../models/questionnaire";
import ENDPOINTS from "../../../utils/endpoints";
import useAnalytics from "hooks/analytics";
import useBookingFlowRedesign from "hooks/useBookingFlowRedesign";
import { trackBookingFlowPersonalFormFilled } from "utils/analytics/booking-flow-tracking";
import { UserActionEvent } from "utils/analytics/events";
import Question from "models/question";
import { normalizePostalCode } from "../../../utils/format";
import {
    getScreeningFlow,
    setIsEngageAppointment,
} from "utils/session-storage";
import { SCREENING_FLOW } from "models/screening-flow";
import { upcomingScreeningAppointmentForFlow } from "models/user";

interface PersonalInfoProps {
    onSubmit: () => void;
}

const extractBodyFromQuestionnaire = (
    questionnaire: Questionnaire,
    isEngageCodeLinkedToUser: boolean
) => {
    const ramqQuestion = questionnaire.questions.find(
        ({ key }) => key === "ramqNumber"
    );
    const ramqExpDate = questionnaire.questions.find(
        ({ key }) => key === "ramqExpDate"
    );
    const birthDateQuestion = questionnaire.questions.find(
        ({ key }) => key === "birthDate"
    );
    const phoneQuestion = questionnaire.questions.find(
        ({ key }) => key === "phone"
    );
    const professionQuestion = questionnaire.questions.find(
        ({ key }) => key === "profession"
    );
    const addressQuestion = questionnaire.questions.find(
        ({ key }) => key === "address"
    );
    const cityQuestion = questionnaire.questions.find(
        ({ key }) => key === "city"
    );
    const zipCodeQuestion = questionnaire.questions.find(
        ({ key }) => key === "zipCode"
    );
    const countryQuestion = questionnaire.questions.find(
        ({ key }) => key === "country"
    );
    const selfDefinedGenderQuestion = questionnaire.questions.find(
        ({ key }) => key === "selfDefinedGender"
    );
    const bornGenderQuestion = questionnaire.questions.find(
        ({ key }) => key === "bornGender"
    );

    const engageCodeQuestion = questionnaire.questions.find(
        ({ key }) => key === "engageCode"
    );

    return {
        ramqNumber: ramqQuestion?.answer.rawValue || null, // force null instead of other empty values

        ramqExpDate: ramqQuestion?.answer.rawValue
            ? ramqExpDate?.answer.rawValue
            : null, // only use value if ramqNumber is provided too
        birthDate: birthDateQuestion?.answer.rawValue,
        phone: phoneQuestion?.answer.rawValue,
        profession: professionQuestion?.answer.rawValue,
        address: addressQuestion?.answer.rawValue,
        city: cityQuestion?.answer.rawValue,
        zipCode: normalizePostalCode(zipCodeQuestion?.answer.rawValue ?? ""),
        country: countryQuestion?.answer.rawValue,
        selfDefinedGender: selfDefinedGenderQuestion?.answer.rawValue,
        bornGender: bornGenderQuestion?.answer.rawValue,
        engageCode: isEngageCodeLinkedToUser
            ? undefined
            : engageCodeQuestion?.answer.rawValue,
    };
};

const PersonalInfo: React.FunctionComponent<PersonalInfoProps> = ({
    onSubmit,
}: PersonalInfoProps) => {
    const [authUser, setAuthUser] = useAuthUser();
    const { translations } = useLanguage();
    const t = translations.infoConfirmationSection;
    const [analytics] = useAnalytics();
    const isBookingFlowRedesign = useBookingFlowRedesign();
    const [isUpdating, setIsUpdating] = useState(false);
    const isHpvFlow = getScreeningFlow() === SCREENING_FLOW.HPV;

    // Network

    const updateUser = async (questionnaire: Questionnaire) => {
        if (!authUser) return;

        const body = extractBodyFromQuestionnaire(
            questionnaire,
            !!authUser?.engageParticipant
        );

        const engageParticipantQuestion = questionnaire.questions.find(
            ({ key }) => key === "engageParticipant"
        );
        setIsEngageAppointment(
            engageParticipantQuestion?.answer?.rawValue === "yes"
        );

        if (!body.zipCode || !body.city || !body.address) {
            return;
        }

        setIsUpdating(true);
        try {
            const { data } = await Axios.put(ENDPOINTS.ME, body);
            setAuthUser({ ...authUser, ...data });

            const screeningFlow = getScreeningFlow();
            const isUpdatingExistingAppointment =
                !!authUser &&
                !!screeningFlow &&
                !!upcomingScreeningAppointmentForFlow(authUser, screeningFlow);

            if (isBookingFlowRedesign && !isUpdatingExistingAppointment) {
                trackBookingFlowPersonalFormFilled(analytics);
            } else if (!isBookingFlowRedesign) {
                analytics
                    ?.cdp()
                    ?.trackEvent(UserActionEvent.ScreeningInformationSubmitted);
            }

            onSubmit();
        } finally {
            setIsUpdating(false);
        }
    };

    let questions: Question[] = [];

    if (authUser) {
        const engageQuestions = [
            {
                id: 21,
                type: "radio",
                key: "engageParticipant",
                index: "12",
                isOptional: true,
                enTranslation:
                    "Is this STI testing visit a component of your participation in the Engage 2.0 and MECS studies?",
                frTranslation:
                    "Cette visite de dépistage ITSS fait-elle partie de votre participation aux études Engage 2.0 et MECS ?",
                answers: [
                    {
                        rawValue: "yes",
                        frTranslation: "Oui",
                        enTranslation: "Yes",
                        order: 1,
                    },
                    {
                        rawValue: "no",
                        frTranslation: "Non",
                        enTranslation: "No",
                        order: 2,
                    },
                ],
                answer: authUser?.engageParticipant
                    ? {
                          rawValue: "yes",
                          frTranslation: "Oui",
                          enTranslation: "Yes",
                      }
                    : undefined,
            },
            {
                id: 22,
                type: "text",
                key: "engageCode",
                index: "12a",
                isOptional: true,
                enTranslation:
                    "Please enter your Engage and MECS participant code",
                frTranslation:
                    "Veuillez entrer votre code de participant Engage et MECS",
                maxlength: 5,
                answers: [],
                answer: {
                    rawValue:
                        authUser?.engageParticipant?.engageCode ?? undefined,
                },
            },
        ];
        const personalInfoQuestions = buildInfoQuestionnaire(authUser, {
            isHpvFlow,
        });

        questions = isHpvFlow
            ? personalInfoQuestions
            : ([...personalInfoQuestions, ...engageQuestions] as Question[]);
    }
    const questionnaire = {
        questions: questions,
    };

    // Rendering
    return (
        <div>
            <h2 className="container">{t.header}</h2>
            <QuestionnaireForm
                questionnaire={questionnaire}
                onCompleteQuestionnaire={updateUser}
                loading={isUpdating}
            />
        </div>
    );
};

export default PersonalInfo;
